import React, { useState, useEffect } from "react";
import { XIcon, SearchIcon, UsersIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { getCurrentFriends, Friend } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface InviteCollaboratorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite?: (friendId: string) => void;
}

export const InviteCollaboratorDialog: React.FC<
  InviteCollaboratorDialogProps
> = ({ isOpen, onClose, onInvite }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [invitedFriends, setInvitedFriends] = useState<Set<string>>(new Set());
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(false);

  // Load friends when dialog opens
  useEffect(() => {
    if (isOpen && user?.id) {
      loadFriends();
    }
  }, [isOpen, user?.id]);

  const loadFriends = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const result = await getCurrentFriends(user.id);

      if (result.success && result.data) {
        setFriends(result.data);
      } else {
        toast.error(result.error || "Gagal memuat daftar teman");
      }
    } catch (error) {
      console.error("Error loading friends:", error);
      toast.error("Terjadi kesalahan saat memuat daftar teman");
    } finally {
      setLoading(false);
    }
  };

  // Filter friends based on search query
  const filteredFriends = friends.filter(
    friend =>
      friend.friend_profiles?.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      friend.friend_profiles?.asal_sekolah
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      friend.friend_profiles?.role
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  // Get avatar URL with fallback
  const getAvatarUrl = (friend: Friend) => {
    if (friend.friend_profiles?.avatar_url) {
      return friend.friend_profiles.avatar_url;
    }
    return "/avatars/ava1.png";
  };

  const handleInvite = (friendId: string) => {
    setInvitedFriends(prev => new Set([...prev, friendId]));
    if (onInvite) {
      onInvite(friendId);
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setInvitedFriends(new Set());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full relative shadow-xl transform transition-all duration-300 scale-100 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-lg flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-[#373e46]">
              Invite Collaborator
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search Input */}
        <div className="relative mb-4">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search friend"
            className="w-full pl-10 h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors"
          />
        </div>

        {/* Friends List */}
        <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
          {loading ? (
            /* Loading State */
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 animate-pulse"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                  <div className="w-16 h-8 bg-gray-200 rounded-xl"></div>
                </div>
              ))}
            </div>
          ) : filteredFriends.length > 0 ? (
            filteredFriends.map(friend => (
              <div
                key={friend.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200"
              >
                {/* Avatar */}
                <Avatar className="w-12 h-12 flex-shrink-0">
                  <AvatarFallback>
                    <img
                      className="w-full h-full object-cover"
                      alt={`${friend.friend_profiles?.name || "Friend"} Avatar`}
                      src={getAvatarUrl(friend)}
                    />
                  </AvatarFallback>
                </Avatar>

                {/* Friend Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-[#373e46] text-sm truncate">
                    {friend.friend_profiles?.name || "Unknown User"}
                  </h4>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="truncate">
                      {friend.friend_profiles?.asal_sekolah || "Unknown School"}
                      {friend.friend_profiles?.role &&
                        `, ${friend.friend_profiles.role}`}
                    </span>
                  </div>
                </div>

                {/* Invite Button */}
                <Button
                  onClick={() => handleInvite(friend.friend_id)}
                  disabled={invitedFriends.has(friend.friend_id)}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 text-sm ${
                    invitedFriends.has(friend.friend_id)
                      ? "bg-green-100 text-green-800 cursor-not-allowed"
                      : "bg-[#373e46] hover:bg-[#2a2a2a] text-white hover:shadow-lg"
                  }`}
                >
                  {invitedFriends.has(friend.friend_id) ? "Invited" : "Invite"}
                </Button>
              </div>
            ))
          ) : (
            /* Empty State */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-[#373e46] mb-2">
                {searchQuery
                  ? "No friends found"
                  : friends.length === 0
                    ? "No friends available"
                    : "No matching friends"}
              </h4>
              <p className="text-gray-500 text-sm">
                {searchQuery
                  ? `No friends match "${searchQuery}"`
                  : friends.length === 0
                    ? "Add friends to invite them for collaboration"
                    : "Try a different search term"}
              </p>
              {friends.length === 0 && !loading && (
                <Button
                  onClick={() => {
                    handleClose();
                    // Navigate to collaboration page to add friends
                    window.location.href = "/collaboration";
                  }}
                  className="mt-4 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg text-sm"
                >
                  Add Friends
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {invitedFriends.size > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              {invitedFriends.size} friend{invitedFriends.size > 1 ? "s" : ""}{" "}
              invited for collaboration
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
